import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from '../../SvgIcon';
import { CompIcon } from '../../CompIcon';
import { TeamIcon } from '../../TeamIcon';
import { getDropdownPosition, getSelectedComp } from './utils';

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {Function} [props.itemToString] - Item to string.
 * @param {any} [props.initialSelectedItem] - Initial selected item.
 * @param {any} [props.activeItem] - Active item from state.
 * @param {Function} [props.onSelectedItemChange] - On change callback.
 * @param {boolean} [props.isDisabled] - Is select disabled.
 * @returns {React.Element} Component.
 */
export const SelectDesktop = (props) => {
    const selectRef = useRef();
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
        display: 'none'
    });

    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps
    } = useSelect({
        items: props.items,
        initialSelectedItem: props.initialSelectedItem,
        selectedItem: props.activeItem,
        // @ts-ignore
        onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );

            if (typeof props.onSelectedItemChange === 'function') {
                props.onSelectedItemChange({
                    selectedItem: newSelectedItem
                });
            }
        }
    });

    const currentRound =
        typeof props?.currentRoundIndex !== 'undefined'
            ? props.currentRoundIndex
            : false;

    const isDisabled = props?.isDisabled;

    /**
     * @param {any} item - Item.
     * @returns {string} Text.
     */
    const getItemText = (item) =>
        props.itemToString ? props.itemToString(item) : item;

    useLayoutEffect(() => {
        setDropdownPosition(getDropdownPosition(isOpen, selectRef));
    }, [isOpen]);

    const selectedComp = useMemo(
        () => getSelectedComp(selectedItem?.providerId, props.showCompLogo),
        [selectedItem, props.showCompLogo]
    );

    const idifiedLabel = PULSE.app.common.idifyString(props.label);

    const toggleButtonProps = {
        id: `${idifiedLabel}-dropdown-button`,
        'aria-labelledby': !props.hideLabel
            ? `${idifiedLabel}-dropdown-label`
            : false,
        'aria-label': props.hideLabel ? props.label : false
    };

    return (
        <div
            className={classNames(
                'select',
                {
                    'select--open': isOpen
                },
                {
                    'select--slim': props.displaySlim
                }
            )}
            ref={selectRef}
        >
            <button
                type="button"
                {...getToggleButtonProps(toggleButtonProps)}
                className="select__display"
                disabled={isDisabled}
            >
                {props.showTeamLogo && selectedItem.teamAbbr && (
                    <TeamIcon
                        className="select__option-logo-icon"
                        providerId={selectedItem.providerId}
                        theme="notext"
                    />
                )}
                {props.showCompLogo && (
                    <span
                        className={`competition-nav__comp-icon competition-nav__comp-icon--${
                            selectedComp?.logoCode?.toLowerCase() ??
                            selectedComp?.code?.toLowerCase() ??
                            selectedItem?.logoCode?.toLowerCase() ??
                            selectedItem?.code?.toLowerCase() ??
                            'afl'
                        }`}
                    >
                        <CompIcon
                            providerId={
                                selectedComp?.providerId ??
                                selectedItem?.providerId
                            }
                            className="icon"
                        />
                    </span>
                )}
                <span className="select__content">
                    {props.label && !props.hideLabel ? (
                        <label
                            {...getLabelProps({
                                id: `${idifiedLabel}-dropdown-label`
                            })}
                            className="select__label"
                        >
                            {props.label}
                        </label>
                    ) : null}
                    <span className="select__current">
                        <span className="select__current-text">
                            {getItemText(selectedItem) || ''}
                        </span>
                        <SvgIcon
                            className="select__chevron"
                            icon="chevron-down"
                        />
                    </span>
                </span>
            </button>
            {ReactDOM.createPortal(
                <div
                    style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        zIndex: 80,
                        display: dropdownPosition.display
                    }}
                >
                    <div className="select__options-wrapper">
                        <ul
                            {...getMenuProps(
                                {
                                    'aria-labelledby': `${idifiedLabel}-dropdown-label`
                                },
                                { suppressRefError: true }
                            )}
                            className={classNames('select__options-list', {
                                'u-hide': !isOpen
                            })}
                        >
                            {isOpen &&
                                props.items.map((item, index) => {
                                    const itemText = getItemText(item);
                                    const isSelected =
                                        getItemText(selectedItem) === itemText;

                                    return (
                                        <li
                                            key={`${itemText}${index}`}
                                            {...getItemProps({ item, index })}
                                            className={classNames(
                                                'select__option',
                                                {
                                                    'select__option--selected':
                                                        isSelected,
                                                    'select__option--current':
                                                        currentRound === index,
                                                    'select__option--highlighted':
                                                        highlightedIndex ===
                                                        index
                                                }
                                            )}
                                        >
                                            <div className="select__option-logo-name-wrapper">
                                                {props.showTeamLogo &&
                                                    item.teamAbbr && (
                                                        <>
                                                            <TeamIcon
                                                                className="select__option-logo-icon select__option-logo-icon--normal"
                                                                providerId={
                                                                    item.providerId
                                                                }
                                                                theme="notext"
                                                            />
                                                            <TeamIcon
                                                                className="select__option-logo-icon select__option-logo-icon--light"
                                                                providerId={
                                                                    item.providerId
                                                                }
                                                                theme={
                                                                    PULSE.app
                                                                        .common
                                                                        .team
                                                                        .filterItemTeamTheme[
                                                                        PULSE.app.common.team.getTeamAbbr(
                                                                            item.providerId
                                                                        )
                                                                    ] ?? 'light'
                                                                }
                                                            />
                                                        </>
                                                    )}
                                                <span className="item-text">
                                                    {itemText}
                                                </span>
                                            </div>

                                            {isSelected &&
                                                !props.showTotals && (
                                                    <SvgIcon
                                                        icon="checkmark"
                                                        className="select__option-button-icon"
                                                    />
                                                )}

                                            {props.showTotals &&
                                                item.total !== null && (
                                                    <div className="select__option-total">
                                                        {item.total}
                                                    </div>
                                                )}
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

SelectDesktop.propTypes = {
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    initialSelectedItem: PropTypes.any,
    activeItem: PropTypes.any,
    onSelectedItemChange: PropTypes.func,
    currentRoundIndex: PropTypes.number,
    showCompLogo: PropTypes.bool,
    isDisabled: PropTypes.bool,
    hideLabel: PropTypes.bool,
    showTeamLogo: PropTypes.bool,
    displaySlim: PropTypes.bool,
    showTotals: PropTypes.bool
};
